import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Lead, Stats } from '../types';
import { Dashboard } from '../features/leads/components/Dashboard';
import { LeadList } from '../features/leads/components/LeadList';
import { LeadListToolbar } from '../features/leads/components/LeadListToolbar';
import { LeadBulkActionBar } from '../features/leads/components/LeadBulkActionBar';
import {
  ALL_FILTER_VALUE,
  NO_CATEGORY_FILTER_VALUE,
  type LeadFilterState,
} from '../features/leads/components/LeadFilterPopover';
import { PipelineBoard, type PipelineMoveTarget } from '../features/leads/components/PipelineBoard';
import { CreateLeadModal } from '../features/leads/components/CreateLeadModal';
import { LeadLostModal } from '../features/leads/components/LeadLostModal';
import { LeadPipelineQuickEditModal } from '../features/leads/components/LeadPipelineQuickEditModal';
import { AppTopBar } from '../components/AppTopBar';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { AppToastViewport } from '../components/AppToastViewport';
import { MergeProjectModal } from '../features/leads/components/MergeProjectModal';
import { mergeProjectSummary, type MergeProjectResult } from '../features/leads/utils/mergeProjectSummary';
import { useCapabilities } from '../hooks/useCapabilities';
import {
  OFFER_QUICK_START_PARAM,
  offerQuickStartTargetPath,
  useOfferQuickStartAccess,
} from '../features/leads/useOfferQuickStart';
import { Plus } from 'lucide-react';
import { PageLoadingState } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { leadDetailPath } from '../utils/leadRoutes';
import { projectListViewFromSlug, projectsListPath, type ProjectListView } from '../utils/projectRoutes';
import { motion, AnimatePresence } from 'motion/react';
import { useToasts } from '../hooks/useToasts';
import {
  defaultDirectionForSortKey,
  downloadCsv,
  leadsToCsv,
  matchesLeadDateRange,
  parseLeadDateRange,
  parseLeadSort,
  serializeLeadSort,
  sortLeads,
  type LeadSortKey,
} from '../features/leads/utils/leadListView';
import type { LeadPipeline } from '../types';
import type { ProjectValue } from '../features/leads/utils/projectValue';

function resolveProjectListView(pathname: string): ProjectListView {
  const segment = pathname.split('/').pop();
  return projectListViewFromSlug(segment) ?? 'pipeline';
}

const searchableText = (lead: Lead): string =>
  [
    lead.name,
    lead.lead_title,
    lead.display_id,
    lead.address,
    lead.notes,
    lead.notiz?.content,
  ]
    .map((part) => String(part ?? ''))
    .join(' ')
    .toLowerCase();

export function LeadsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = resolveProjectListView(location.pathname);
  const { toasts, pushToast, dismissToast } = useToasts();
  const { can } = useCapabilities();
  const canEditProjects = can('web.projekte.edit');
  const offerQuickStart = useOfferQuickStartAccess();
  const [mergeSource, setMergeSource] = React.useState<Lead | null>(null);

  const setViewMode = (view: ProjectListView) => {
    navigate({ pathname: projectsListPath(view), search: searchParams.toString() });
  };

  /** Alle Listen-Parameter ersetzen den History-Eintrag, damit „Zurück“ die Seite verlässt. */
  const updateParams = React.useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [leads, setLeads] = React.useState<Lead[]>([]);
  /** `board` = open-only slim payload for pipeline; `full` = complete list for list/dashboard. */
  const [leadsScope, setLeadsScope] = React.useState<'none' | 'board' | 'full'>('none');
  const [stats, setStats] = React.useState<Stats | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  /** `offer` = Schnellstart „Neues Angebot“ (Projekt + Angebot anlegen, danach Editor). */
  const [createIntent, setCreateIntent] = React.useState<'project' | 'offer'>('project');
  const [prefillContactId, setPrefillContactId] = React.useState<string | null>(null);
  const [prefillOrganizationId, setPrefillOrganizationId] = React.useState<string | null>(null);

  // When opened with ?create=1 (e.g. from the dashboard "+ Anfrage" button),
  // open a blank CreateLeadModal.
  React.useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    setPrefillContactId(null);
    setPrefillOrganizationId(null);
    setCreateIntent('project');
    setIsCreateModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ?neuesAngebot=1 (Schnellstart „Neues Angebot“): Kunde wählen → Projekt + Angebot → Editor.
  // Erst entscheiden, wenn Module und Rechte geladen sind; ohne Zugriff nur den Parameter entfernen.
  // Die Vorbelegung bleibt unangetastet (geschlossen ist sie leer), damit ?createFor= zusätzlich
  // den Kunden vorbelegen kann.
  const offerQuickStartLoaded = offerQuickStart.loaded;
  const offerQuickStartAllowed = offerQuickStart.allowed;
  React.useEffect(() => {
    if (searchParams.get(OFFER_QUICK_START_PARAM) !== '1') return;
    if (!offerQuickStartLoaded) return;
    if (offerQuickStartAllowed) {
      setCreateIntent('offer');
      setIsCreateModalOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete(OFFER_QUICK_START_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, offerQuickStartLoaded, offerQuickStartAllowed]);

  // When opened with ?createFor=contact:<id> or ?createFor=organization:<id>,
  // open the CreateLeadModal prefilled with that contact / org context.
  React.useEffect(() => {
    const raw = searchParams.get('createFor');
    if (!raw) return;
    const [kind, id] = raw.split(':');
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        if (kind === 'contact') {
          if (!cancelled) {
            setPrefillOrganizationId(null);
            setPrefillContactId(id);
            setIsCreateModalOpen(true);
          }
        } else if (kind === 'organization') {
          const res = await fetch(`/api/organizations/${id}/contacts`);
          const list = res.ok ? ((await res.json()) as Array<{ id: string | number; is_primary?: boolean }>) : [];
          const primary = list.find((c) => c.is_primary) ?? list[0];
          if (!cancelled) {
            setPrefillOrganizationId(id);
            setPrefillContactId(primary ? String(primary.id) : null);
            setIsCreateModalOpen(true);
          }
        }
      } finally {
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete('createFor');
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [deletingLeadId, setDeletingLeadId] = React.useState<string | number | null>(null);
  const [pipelines, setPipelines] = React.useState<LeadPipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = React.useState<string | null>(null);
  const [lossModalLead, setLossModalLead] = React.useState<Lead | null>(null);
  const [lossModalSaving, setLossModalSaving] = React.useState(false);
  const [lossModalError, setLossModalError] = React.useState<string | null>(null);
  const [pipelineModalOpen, setPipelineModalOpen] = React.useState(false);
  const [leadCategories, setLeadCategories] = React.useState<
    Array<{ key: string; label: string; pipeline_id?: string | null }>
  >([]);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [deleteTargets, setDeleteTargets] = React.useState<Lead[]>([]);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const pipelineFromUrl = searchParams.get('pipeline');

  // Suche lebt lokal und wird verzögert in die URL geschrieben — sonst schreibt
  // jeder Tastendruck einen History-Eintrag und die Liste ruckelt.
  const searchParamValue = searchParams.get('q') ?? '';
  const [searchTerm, setSearchTerm] = React.useState(searchParamValue);
  const lastPushedSearch = React.useRef(searchParamValue);

  React.useEffect(() => {
    if (searchParamValue === lastPushedSearch.current) return;
    lastPushedSearch.current = searchParamValue;
    setSearchTerm(searchParamValue);
  }, [searchParamValue]);

  React.useEffect(() => {
    if (searchTerm === searchParamValue) return;
    const timer = setTimeout(() => {
      lastPushedSearch.current = searchTerm;
      updateParams((params) => {
        if (searchTerm) params.set('q', searchTerm);
        else params.delete('q');
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm, searchParamValue, updateParams]);

  const filters: LeadFilterState = React.useMemo(
    () => ({
      category: searchParams.get('category') || ALL_FILTER_VALUE,
      status: searchParams.get('status') || ALL_FILTER_VALUE,
      dateRange: parseLeadDateRange(searchParams.get('range')),
    }),
    [searchParams],
  );

  const sort = React.useMemo(() => parseLeadSort(searchParams.get('sort')), [searchParams]);

  const handleFiltersChange = React.useCallback(
    (next: Partial<LeadFilterState>) => {
      updateParams((params) => {
        if (next.category !== undefined) {
          if (next.category === ALL_FILTER_VALUE) params.delete('category');
          else params.set('category', next.category);
        }
        if (next.status !== undefined) {
          if (next.status === ALL_FILTER_VALUE) params.delete('status');
          else params.set('status', next.status);
        }
        if (next.dateRange !== undefined) {
          if (next.dateRange === 'all') params.delete('range');
          else params.set('range', next.dateRange);
        }
      });
    },
    [updateParams],
  );

  const handleResetFilters = React.useCallback(() => {
    lastPushedSearch.current = '';
    setSearchTerm('');
    updateParams((params) => {
      params.delete('q');
      params.delete('category');
      params.delete('status');
      params.delete('range');
    });
  }, [updateParams]);

  const handleSortChange = React.useCallback(
    (key: LeadSortKey) => {
      const next =
        sort.key === key
          ? { key, direction: sort.direction === 'asc' ? ('desc' as const) : ('asc' as const) }
          : { key, direction: defaultDirectionForSortKey(key) };
      updateParams((params) => {
        const serialized = serializeLeadSort(next);
        if (serialized) params.set('sort', serialized);
        else params.delete('sort');
      });
    },
    [sort, updateParams],
  );

  const mergeBoardTotals = React.useCallback(async (leadIds: Array<string | number>) => {
    const ids = leadIds.map((id) => String(id)).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const res = await fetch('/api/leads/board-totals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) return;
      const body = (await res.json()) as {
        totals?: Record<string, number | null>;
        values?: Record<string, ProjectValue | null>;
      };
      const totals = body.totals;
      if (!totals || typeof totals !== 'object') return;
      const values = body.values ?? {};
      setLeads((prev) =>
        prev.map((lead) => {
          const key = String(lead.id);
          if (!(key in totals)) return lead;
          const next = totals[key];
          const nextValue = values[key] ?? null;
          if (next === lead.net_total && JSON.stringify(nextValue) === JSON.stringify(lead.project_value ?? null)) {
            return lead;
          }
          return { ...lead, net_total: next ?? undefined, project_value: nextValue };
        }),
      );
    } catch {
      /* ignore — cards stay without totals */
    }
  }, []);

  const fetchLeadsForScope = React.useCallback(
    async (scope: 'board' | 'full') => {
      const res = await fetch(scope === 'board' ? '/api/leads?scope=board' : '/api/leads');
      const leadsData = await res.json();
      if (!res.ok || !Array.isArray(leadsData)) {
        throw new Error(
          !res.ok
            ? (leadsData as { error?: string })?.error || `Projekte laden fehlgeschlagen (${res.status})`
            : 'Ungültige Projektliste',
        );
      }
      const nextLeads = leadsData as Lead[];
      setLeads(nextLeads);
      setLeadsScope(scope);
      if (scope === 'board') {
        void mergeBoardTotals(nextLeads.map((l) => l.id));
      }
      return nextLeads;
    },
    [mergeBoardTotals],
  );

  const fetchData = async () => {
    const initialScope: 'board' | 'full' = viewMode === 'pipeline' ? 'board' : 'full';
    try {
      const [leadsRes, statsRes, pipelinesRes, categoriesRes] = await Promise.all([
        fetch(initialScope === 'board' ? '/api/leads?scope=board' : '/api/leads'),
        fetch('/api/stats'),
        fetch('/api/settings/lead-pipelines'),
        fetch('/api/settings/lead-categories'),
      ]);
      const leadsData = await leadsRes.json();
      const statsData = await statsRes.json();
      if (!leadsRes.ok || !Array.isArray(leadsData)) {
        throw new Error(
          !leadsRes.ok
            ? (leadsData as { error?: string })?.error || `Projekte laden fehlgeschlagen (${leadsRes.status})`
            : 'Ungültige Projektliste',
        );
      }
      const nextLeads = leadsData as Lead[];
      setLeads(nextLeads);
      setLeadsScope(initialScope);
      setStats(statsData);
      if (initialScope === 'board') {
        void mergeBoardTotals(nextLeads.map((l) => l.id));
      }
      let nextPipelines: LeadPipeline[] = [];
      if (pipelinesRes.ok) {
        const p = await pipelinesRes.json();
        if (Array.isArray(p)) nextPipelines = p as LeadPipeline[];
      }
      setPipelines(nextPipelines);
      const defId = nextPipelines.find((x) => x.is_default)?.id ?? nextPipelines[0]?.id ?? null;
      const urlOk = pipelineFromUrl && nextPipelines.some((x) => x.id === pipelineFromUrl);
      setSelectedPipelineId(urlOk ? pipelineFromUrl : defId);
      if (categoriesRes.ok) {
        const cats = await categoriesRes.json();
        if (Array.isArray(cats)) {
          setLeadCategories(
            cats.map((row: { key?: string; id?: string; label?: string; pipeline_id?: string | null }) => ({
              key: String(row.key ?? row.id ?? ''),
              label: String(row.label ?? ''),
              pipeline_id: row.pipeline_id ?? null,
            })).filter((c) => c.key && c.label),
          );
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedPipeline =
    pipelines.find((p) => p.id === selectedPipelineId) ??
    pipelines.find((p) => p.is_default) ??
    pipelines[0] ??
    null;

  const defaultPipelineId = pipelines.find((p) => p.is_default)?.id ?? pipelines[0]?.id ?? null;

  const categoriesForSelectedPipeline = React.useMemo(() => {
    if (!selectedPipeline) return leadCategories;
    return leadCategories.filter((c) => {
      const pid = c.pipeline_id ?? defaultPipelineId;
      return pid === selectedPipeline.id;
    });
  }, [leadCategories, selectedPipeline, defaultPipelineId]);

  const isLeadWithoutCategory = React.useCallback((lead: Lead) => {
    const key = lead.category_key;
    const label = lead.category;
    const hasKey = key != null && String(key).trim() !== '';
    const hasLabel = label != null && String(label).trim() !== '';
    return !hasKey && !hasLabel;
  }, []);

  const belongsToPipelineForNoCategory = React.useCallback(
    (lead: Lead, pipelineId: string) => {
      const pipeline = pipelines.find((p) => p.id === pipelineId);
      if (!pipeline) return false;

      // Unkategorisierte Projekte sind „direkt“ einer Pipeline zugeordnet, falls `pipeline_id` gesetzt ist.
      if (lead.pipeline_id != null && String(lead.pipeline_id) === String(pipeline.id)) return true;

      // Legacy / ungezuordnete Projekte ohne `pipeline_id` werden nur der Default-Pipeline zugeordnet.
      return lead.pipeline_id == null && !lead.category_key && pipeline.is_default;
    },
    [pipelines],
  );

  const hasNoCategory = React.useMemo(() => {
    const pipelineContextActive =
      (viewMode === 'pipeline' || (viewMode === 'list' && pipelines.length > 1)) && selectedPipeline != null;

    if (!pipelineContextActive || !selectedPipeline) return leads.some(isLeadWithoutCategory);

    return leads.some((lead) => isLeadWithoutCategory(lead) && belongsToPipelineForNoCategory(lead, selectedPipeline.id));
  }, [leads, isLeadWithoutCategory, belongsToPipelineForNoCategory, selectedPipeline, viewMode, pipelines.length]);

  const setPipelineSelection = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    const def = pipelines.find((p) => p.is_default)?.id;
    const stillValid =
      filters.category === ALL_FILTER_VALUE ||
      (filters.category === NO_CATEGORY_FILTER_VALUE
        ? leads.some((lead) => isLeadWithoutCategory(lead) && belongsToPipelineForNoCategory(lead, pipelineId))
        : false) ||
      leadCategories.some((c) => {
        if (c.key !== filters.category) return false;
        const pid = c.pipeline_id ?? def ?? null;
        return pid === pipelineId;
      });
    updateParams((params) => {
      if (pipelineId && pipelineId !== def) params.set('pipeline', pipelineId);
      else params.delete('pipeline');
      // Kategorie einer anderen Pipeline würde die Ansicht leer erscheinen lassen.
      if (!stillValid) params.delete('category');
    });
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  /** List/dashboard need the full payload; pipeline can reuse full data or use the slim board fetch. */
  React.useEffect(() => {
    if (leadsScope === 'none') return;
    if (viewMode === 'pipeline') return;
    if (leadsScope === 'full') return;
    let cancelled = false;
    setIsLoading(true);
    void fetchLeadsForScope('full')
      .catch((error) => {
        console.error('Failed to fetch full project list:', error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, leadsScope, fetchLeadsForScope]);

  const handleCreateLead = (newLead: Lead) => {
    setLeads((prev) => [newLead, ...prev]);
    fetch('/api/stats').then((res) => res.json()).then(setStats);
    navigate(createIntent === 'offer' ? offerQuickStartTargetPath(newLead) : leadDetailPath(newLead));
  };

  const openCreateProject = () => {
    setCreateIntent('project');
    setIsCreateModalOpen(true);
  };

  const handleMoveLead = async (id: string | number, target: PipelineMoveTarget) => {
    if (target.kind === 'outcome' && target.outcome === 'lost') {
      const lead = leads.find((l) => String(l.id) === String(id)) ?? null;
      if (lead) {
        setLossModalError(null);
        setLossModalLead(lead);
      }
      return;
    }

    const body =
      target.kind === 'stage'
        ? { stage_id: target.stageId, outcome: 'open' as const }
        : { outcome: target.outcome };

    const stageLabel =
      target.kind === 'stage'
        ? selectedPipeline?.stages.find((s) => s.id === target.stageId)?.label
        : undefined;

    setLeads((prev) =>
      prev.map((l) =>
        String(l.id) === String(id)
          ? {
              ...l,
              ...(target.kind === 'stage'
                ? { stage_id: target.stageId, outcome: 'open', status: stageLabel ?? l.status }
                : {
                    outcome: target.outcome,
                    status: target.outcome === 'won' ? 'Gewonnen' : 'Verloren',
                  }),
            }
          : l,
      ),
    );

    try {
      const response = await fetch(`/api/leads/${encodeURIComponent(String(id))}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error('Failed to move lead');
      }
      fetch('/api/stats')
        .then((res) => res.json())
        .then(setStats);
    } catch (error) {
      console.error('Error moving lead:', error);
      fetchData();
    }
  };

  const handleLossModalConfirm = async (payload: {
    loss_reason: string;
    loss_comment?: string;
    lost_at: string;
  }) => {
    if (!lossModalLead) return;
    setLossModalSaving(true);
    setLossModalError(null);
    try {
      const res = await fetch(`/api/leads/${lossModalLead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcome: 'lost',
          status: 'Verloren',
          loss_reason: payload.loss_reason,
          loss_comment: payload.loss_comment,
          lost_at: payload.lost_at,
        }),
      });
      if (!res.ok) {
        const raw = await res.text();
        let msg = raw || `Fehler ${res.status}`;
        try {
          const j = JSON.parse(raw) as { error?: string };
          if (j?.error) msg = j.error;
        } catch {
          /* use raw */
        }
        setLossModalError(msg);
        return;
      }
      const body = (await res.json().catch(() => ({}))) as {
        lost_at?: string;
        lost_by?: string;
        lost_by_user_id?: string | null;
      };
      setLeads((prev) =>
        prev.map((l) =>
          String(l.id) === String(lossModalLead.id)
            ? {
                ...l,
                outcome: 'lost',
                status: 'Verloren',
                loss_reason: payload.loss_reason,
                loss_comment: payload.loss_comment,
                lost_at: body.lost_at ?? payload.lost_at,
                lost_by: body.lost_by,
                lost_by_user_id: body.lost_by_user_id,
              }
            : l,
        ),
      );
      setLossModalLead(null);
      fetch('/api/stats')
        .then((r) => r.json())
        .then(setStats);
    } finally {
      setLossModalSaving(false);
    }
  };

  const availableStatuses = React.useMemo(() => {
    const unique = new Set<string>();
    leads.forEach((lead) => {
      const status = String(lead.status ?? '').trim();
      if (status) unique.add(status);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'de'));
  }, [leads]);

  // Pipeline-Board und Liste: Suche, Kategorie, Status, Zeitraum
  // (+ Pipeline-Zugehörigkeit in der Pipeline-Ansicht).
  const filteredLeads = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return leads.filter((lead) => {
      if (query && !searchableText(lead).includes(query)) return false;

      if (
        filters.category !== ALL_FILTER_VALUE &&
        (filters.category === NO_CATEGORY_FILTER_VALUE ? !isLeadWithoutCategory(lead) : (
          (lead.category_key ?? '') !== filters.category &&
          lead.category !== filters.category
        ))
      ) {
        return false;
      }

      if (filters.status !== ALL_FILTER_VALUE && lead.status !== filters.status) return false;
      if (!matchesLeadDateRange(lead.created_at, filters.dateRange)) return false;

      if (
        (viewMode === 'pipeline' || (viewMode === 'list' && pipelines.length > 1)) &&
        selectedPipeline
      ) {
        const belongsToPipeline =
          (lead.pipeline_id != null && lead.pipeline_id === selectedPipeline.id) ||
          (lead.pipeline_id == null &&
            categoriesForSelectedPipeline.some(
              (c) => c.key === (lead.category_key ?? '') || c.label === lead.category,
            )) ||
          (lead.pipeline_id == null && !lead.category_key && selectedPipeline.is_default);
        if (!belongsToPipeline) return false;
      }

      return true;
    });
  }, [
    leads,
    searchTerm,
    filters,
    viewMode,
    selectedPipeline,
    categoriesForSelectedPipeline,
    pipelines.length,
  ]);

  const sortedLeads = React.useMemo(() => sortLeads(filteredLeads, sort), [filteredLeads, sort]);

  // Auswahl auf sichtbare Zeilen begrenzen — sonst löscht die Sammelaktion
  // Projekte, die gerade weggefiltert sind.
  React.useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(sortedLeads.map((lead) => String(lead.id)));
      const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [sortedLeads]);

  const handleToggleSelected = React.useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleToggleAllVisible = React.useCallback(
    (selected: boolean) => {
      setSelectedIds(selected ? new Set(sortedLeads.map((lead) => String(lead.id))) : new Set());
    },
    [sortedLeads],
  );

  const selectedLeads = React.useMemo(
    () => sortedLeads.filter((lead) => selectedIds.has(String(lead.id))),
    [sortedLeads, selectedIds],
  );

  const handleExportSelected = () => {
    const rows = selectedLeads.length > 0 ? selectedLeads : sortedLeads;
    downloadCsv(`projekte-${new Date().toISOString().slice(0, 10)}.csv`, leadsToCsv(rows));
    pushToast(
      `${rows.length} ${rows.length === 1 ? 'Projekt' : 'Projekte'} als CSV exportiert.`,
      'success',
    );
  };

  const handleMerged = (result: MergeProjectResult) => {
    const sourceId = String(mergeSource?.id ?? '');
    setMergeSource(null);
    setLeads((prev) => prev.filter((lead) => String(lead.id) !== sourceId));
    setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => id !== sourceId)));
    if (leadsScope !== 'none') {
      void fetchLeadsForScope(leadsScope).catch((error) => console.error('Failed to refresh projects:', error));
    }
    fetch('/api/stats')
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
    const summary = mergeProjectSummary(result.counts);
    pushToast(
      `Projekt überführt in ${result.target_display_id || 'Zielprojekt'}${summary ? ` (${summary})` : ''}.`,
      'success',
      { label: 'Öffnen', onClick: () => navigate(leadDetailPath({ id: result.target_id })) },
    );
  };

  const handleConfirmDelete = async () => {
    const targets = deleteTargets;
    if (targets.length === 0) return;
    setIsDeleting(true);
    if (targets.length === 1) setDeletingLeadId(targets[0].id);

    const deletedIds: string[] = [];
    const failedLabels: string[] = [];

    for (const lead of targets) {
      const id = String(lead.id);
      try {
        const res = await fetch(`/api/leads/${encodeURIComponent(id)}`, { method: 'DELETE' });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body?.error || 'Projekt konnte nicht gelöscht werden.');
        }
        deletedIds.push(id);
      } catch (err) {
        console.error('Failed to delete lead', id, err);
        failedLabels.push(lead.display_id || lead.lead_title || id);
      }
    }

    if (deletedIds.length > 0) {
      const removed = new Set(deletedIds);
      setLeads((prev) => prev.filter((lead) => !removed.has(String(lead.id))));
      setSelectedIds((prev) => new Set(Array.from(prev).filter((id) => !removed.has(id))));
      fetch('/api/stats')
        .then((r) => r.json())
        .then(setStats)
        .catch(() => {});
      pushToast(
        deletedIds.length === 1
          ? 'Projekt in den Papierkorb verschoben. 28 Tage wiederherstellbar.'
          : `${deletedIds.length} Projekte in den Papierkorb verschoben. 28 Tage wiederherstellbar.`,
        'success',
      );
    }

    if (failedLabels.length > 0) {
      pushToast(
        failedLabels.length === 1
          ? `„${failedLabels[0]}“ konnte nicht gelöscht werden.`
          : `${failedLabels.length} Projekte konnten nicht gelöscht werden.`,
        'error',
      );
    }

    setDeletingLeadId(null);
    setIsDeleting(false);
    setDeleteTargets([]);
  };

  const deleteDialogTitle =
    deleteTargets.length > 1 ? `${deleteTargets.length} Projekte löschen?` : 'Projekt löschen?';
  const deleteDialogDescription =
    deleteTargets.length > 1
      ? `${deleteTargets.length} Projekte werden in den Papierkorb verschoben und sind dort 28 Tage lang wiederherstellbar.`
      : `„${deleteTargets[0]?.lead_title || deleteTargets[0]?.display_id || 'Projekt'}“ wird in den Papierkorb verschoben und ist dort 28 Tage lang wiederherstellbar.`;

  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    filters.category !== ALL_FILTER_VALUE ||
    filters.status !== ALL_FILTER_VALUE ||
    filters.dateRange !== 'all';

  return (
    <div className="h-screen flex bg-app-bg font-sans overflow-hidden">
      <AppSidebar active="leads" />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Projekte" />

        <LeadListToolbar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          resultCount={viewMode === 'pipeline' ? filteredLeads.length : sortedLeads.length}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          pipelines={pipelines}
          selectedPipelineId={selectedPipeline?.id ?? null}
          onPipelineChange={setPipelineSelection}
          onOpenPipelineSettings={() => setPipelineModalOpen(true)}
          categories={
            pipelines.length > 1 ? categoriesForSelectedPipeline : leadCategories
          }
          statuses={availableStatuses}
          filters={filters}
          hasNoCategory={hasNoCategory}
          onFiltersChange={handleFiltersChange}
          onResetFilters={handleResetFilters}
          onCreateLead={openCreateProject}
        />

        {/* Content Area — Liste scrollt intern; Pipeline/Dashboard scrollen hier dezent. */}
        <div
          className={
            viewMode === 'list'
              ? 'relative flex-1 min-h-0 overflow-hidden'
              : 'relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain app-scrollbar md:px-4 md:pt-4'
          }
        >
          <AnimatePresence mode="wait">
            {/* On mobile always show list; on desktop respect viewMode */}
            {(viewMode === 'pipeline' || viewMode === 'dashboard') && (
              <motion.div
                key={viewMode}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="hidden min-h-0 flex-1 md:flex md:flex-col"
              >
                {isLoading ? (
                  <PageLoadingState />
                ) : viewMode === 'pipeline' ? (
                  <PipelineBoard
                    leads={filteredLeads}
                    stages={selectedPipeline?.stages ?? []}
                    onSelectLead={(lead) => navigate(leadDetailPath(lead))}
                    onMoveLead={handleMoveLead}
                  />
                ) : (
                  <Dashboard stats={stats} />
                )}
              </motion.div>
            )}
            {/* List view — always visible on mobile, conditionally on desktop */}
            <motion.div
              key="list"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={viewMode === 'list' ? 'block h-full' : 'md:hidden'}
            >
              <LeadList
                leads={sortedLeads}
                isLoading={isLoading}
                sort={sort}
                onSortChange={handleSortChange}
                selectedIds={selectedIds}
                onToggleSelected={handleToggleSelected}
                onToggleAllVisible={handleToggleAllVisible}
                onSelectLead={(lead) => navigate(leadDetailPath(lead))}
                onDeleteLead={(lead) => setDeleteTargets([lead])}
                onMergeLead={canEditProjects ? setMergeSource : undefined}
                detailHref={leadDetailPath}
                deletingLeadId={deletingLeadId}
                hasActiveFilters={hasActiveFilters}
                onResetFilters={handleResetFilters}
                onCreateLead={openCreateProject}
              />
            </motion.div>
          </AnimatePresence>

          {viewMode === 'list' ? (
            <LeadBulkActionBar
              selectedCount={selectedLeads.length}
              onClear={() => setSelectedIds(new Set())}
              onExport={handleExportSelected}
              onDelete={() => setDeleteTargets(selectedLeads)}
              isDeleting={isDeleting}
            />
          ) : null}
        </div>

        {/* Mobile FAB — Neues Projekt */}
        <button
          type="button"
          onClick={openCreateProject}
          className="fixed bottom-20 right-5 z-30 md:hidden h-14 w-14 rounded-full bg-green-600 text-white shadow-lg flex items-center justify-center hover:bg-green-700 active:bg-green-800"
          aria-label="Neues Projekt anlegen"
        >
          <Plus className="w-6 h-6" />
        </button>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <CreateLeadModal
            variant="drawer"
            intent={createIntent}
            onClose={() => {
              setIsCreateModalOpen(false);
              setCreateIntent('project');
              setPrefillContactId(null);
              setPrefillOrganizationId(null);
            }}
            onCreated={handleCreateLead}
            prefillContactId={prefillContactId}
            prefillOrganizationId={prefillOrganizationId}
          />
        )}
      </AnimatePresence>

      <LeadLostModal
        open={lossModalLead != null}
        onClose={() => {
          setLossModalLead(null);
          setLossModalError(null);
        }}
        onConfirm={handleLossModalConfirm}
        isSaving={lossModalSaving}
        error={lossModalError}
      />

      <LeadPipelineQuickEditModal
        open={pipelineModalOpen}
        onClose={() => setPipelineModalOpen(false)}
        pipeline={selectedPipeline}
        onSaved={(next) => {
          setPipelines((prev) =>
            prev.map((p) => (p.id === next.id ? next : p)),
          );
        }}
      />

      <AppConfirmDialog
        open={deleteTargets.length > 0}
        onClose={() => setDeleteTargets([])}
        onConfirm={handleConfirmDelete}
        title={deleteDialogTitle}
        description={deleteDialogDescription}
        confirmLabel={deleteTargets.length > 1 ? 'Alle löschen' : 'Löschen'}
        destructive
        isLoading={isDeleting}
      />

      <MergeProjectModal
        open={mergeSource != null}
        source={mergeSource}
        onClose={() => setMergeSource(null)}
        onMerged={handleMerged}
      />

      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
